import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, orderBy, query } from 'firebase/firestore';
import { FaTrophy, FaSearch, FaShare, FaChevronUp, FaChevronDown, FaMedal, FaFireAlt, FaStar, FaCrown } from 'react-icons/fa';

// Animated confetti effect component (improved visibility)
const Confetti = () => {
  return (
    <div className="fixed inset-0 pointer-events-none z-20" aria-hidden="true">
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          className={`absolute animate-fall 
            w-3 h-3 rounded-full 
            ${i % 5 === 0 ? 'bg-red-500' : 
               i % 5 === 1 ? 'bg-yellow-400' : 
               i % 5 === 2 ? 'bg-blue-500' : 
               i % 5 === 3 ? 'bg-green-500' :
               'bg-purple-500'}`}
          style={{
            left: `${Math.random() * 100}%`,
            top: `-${Math.random() * 20}px`,
            animationDuration: `${Math.random() * 3 + 2}s`,
            animationDelay: `${Math.random() * 2}s`,
          }}
        />
      ))}
    </div>
  );
};

// Trophy component with fixed hover effect
const Trophy = ({ rank }) => {
  const trophyIcons = {
    1: <FaCrown className="text-3xl text-[var(--accent-color)]" />,
    2: <FaMedal className="text-3xl text-gray-300" />,
    3: <FaMedal className="text-3xl text-amber-700" />
  };
  
  return (
    <div className="relative inline-flex items-center">
      <div className="transform transition-all duration-300 hover:scale-125">
        {trophyIcons[rank] || <span className="text-xl font-bold">#{rank}</span>}
      </div>
      {rank <= 3 && (
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--interactive-color)] opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-[var(--interactive-color)]"></span>
        </span>
      )}
    </div>
  );
};

// Silver Medal Color Fix and Top Three Redesign

// Enhanced Top Three component - NEW DESIGN WITHOUT PODIUM
const TopThree = ({ topThree, isLightMode }) => (
  <div className="flex flex-col items-center mb-12">
    {/* Champion Cards - SIMPLIFIED HORIZONTAL DESIGN */}
    <div className="flex flex-wrap justify-center items-end gap-4 w-full max-w-4xl mx-auto">
      {/* Second Place - Left */}
      <div className="w-64 transform transition-all duration-300 hover:scale-105">
        <div className="bg-[var(--surface-color)] text-[var(--text-color)] border-t-4 border-[#4A4E56] rounded-lg shadow-md overflow-hidden">
          <div className="bg-[#4A4E56] text-white py-2 text-center flex justify-center items-center">
            <span className="text-xl mr-2">🥈</span>
            <span className="font-bold">2nd Place</span>
          </div>
          
          <div className="p-4 text-center">
            <img
              className="w-16 h-16 rounded-full border-2 border-[#4A4E56] mx-auto mb-2 object-cover"
              src={topThree[1]?.profilePic || '/profilepicture.png'}
              alt={`${topThree[1]?.firstName || 'N/A'} ${topThree[1]?.lastName || 'N/A'}`}
              onError={(e) => {e.target.src = '/profilepicture.png'}}
            />
            
            <div className="font-bold truncate">{`${topThree[1]?.firstName || 'N/A'} ${topThree[1]?.lastName || 'N/A'}`}</div>
            <div className="text-[#4A4E56] font-bold">{topThree[1]?.score?.toLocaleString() || 0} XP</div>
          </div>
        </div>
      </div>

      {/* First Place - Center (slightly larger) */}
      <div className="w-72 -mb-4 z-10 transform transition-all duration-300 hover:scale-105">
        <div className="bg-[var(--surface-color)] text-[var(--text-color)] border-t-4 border-[#FFD700] rounded-lg shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-gray-900 py-3 text-center flex justify-center items-center">
            <span className="text-2xl mr-2">🏆</span>
            <span className="font-bold text-lg">Champion</span>
          </div>
          
          <div className="p-5 text-center">
            <img
              className="w-20 h-20 rounded-full border-2 border-[#FFD700] mx-auto mb-2 object-cover shadow-md"
              src={topThree[0]?.profilePic || '/profilepicture.png'}
              alt={`${topThree[0]?.firstName || 'N/A'} ${topThree[0]?.lastName || 'N/A'}`}
              onError={(e) => {e.target.src = '/profilepicture.png'}}
            />
            
            <div className="font-bold truncate text-lg">{`${topThree[0]?.firstName || 'N/A'} ${topThree[0]?.lastName || 'N/A'}`}</div>
            <div className="text-amber-600 font-bold text-lg">{topThree[0]?.score?.toLocaleString() || 0} XP</div>
          </div>
        </div>
      </div>

      {/* Third Place - Right */}
      <div className="w-64 transform transition-all duration-300 hover:scale-105">
        <div className="bg-[var(--surface-color)] text-[var(--text-color)] border-t-4 border-[#8B4513] rounded-lg shadow-md overflow-hidden">
          <div className="bg-[#8B4513] text-white py-2 text-center flex justify-center items-center">
            <span className="text-xl mr-2">🥉</span>
            <span className="font-bold">3rd Place</span>
          </div>
          
          <div className="p-4 text-center">
            <img
              className="w-16 h-16 rounded-full border-2 border-[#8B4513] mx-auto mb-2 object-cover"
              src={topThree[2]?.profilePic || '/profilepicture.png'}
              alt={`${topThree[2]?.firstName || 'N/A'} ${topThree[2]?.lastName || 'N/A'}`}
              onError={(e) => {e.target.src = '/profilepicture.png'}}
            />
            
            <div className="font-bold truncate">{`${topThree[2]?.firstName || 'N/A'} ${topThree[2]?.lastName || 'N/A'}`}</div>
            <div className="text-[#8B4513] font-bold">{topThree[2]?.score?.toLocaleString() || 0} XP</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Leaderboard = ({ isLightMode }) => {
  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();

  const [leaderboard, setLeaderboard] = useState([]);
  const [topThree, setTopThree] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleUsers, setVisibleUsers] = useState(10);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    // Show confetti briefly when component mounts
    setShowConfetti(true);
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setIsLoggedIn(true);
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  const fetchLeaderboard = async () => {
    try {
      const leaderboardQuery = query(
        collection(db, 'gameplay'),
        orderBy('score', 'desc')
      );
      const leaderboardSnapshot = await getDocs(leaderboardQuery);
      const leaderboardData = leaderboardSnapshot.docs.map((doc, index) => {
        const data = doc.data();
        return {
          rank: index + 1,
          firstName: data?.firstName || 'N/A',
          lastName: data?.lastName || 'N/A',
          score: data?.score || 0,
          profilePic: data?.photoURL || '/profilepicture.png',
        };
      });

      setLeaderboard(leaderboardData);
      setTopThree(leaderboardData.slice(0, 3));
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, [db]);

  const filteredLeaderboard = leaderboard.filter((user) => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  const showMoreUsers = () => {
    if (showAll) {
      setShowAll(false);
      setVisibleUsers(10);
    } else {
      setShowAll(true);
      setVisibleUsers(filteredLeaderboard.length);
    }
  };

  const handleShare = async () => {
    const leaderboardURL = window.location.href; 
    const title = "Quiz Arcade Leaderboard";
    const text = "Check out the leaderboard on Quiz Arcade and see who's on top!";
  
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: text,
          url: leaderboardURL,
        });
        alert("Leaderboard shared successfully!");
      } catch (error) {
        console.error("Error sharing the leaderboard:", error);
      }
    } else {
      try {
        navigator.clipboard.writeText(leaderboardURL);
        alert("URL copied to clipboard. Share it with your friends!");
      } catch (error) {
        console.error("Failed to copy URL:", error);
      }
    }
  };  

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-[var(--background-color)] text-[var(--text-color)]">
        <div className="flex flex-col items-center">
          <div className="relative w-20 h-20">
            <div className="absolute inset-0 rounded-full border-4 border-[var(--primary-color)] opacity-25"></div>
            <div className="absolute inset-0 rounded-full border-4 border-t-[var(--interactive-color)] border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
          </div>
          <p className="text-lg font-medium mt-4">Loading champions...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return null;
  }

  return (
    <div className="min-h-screen p-6 bg-[var(--background-color)] text-[var(--text-color)] relative overflow-hidden">
      {/* Animated background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-[var(--primary-color)] opacity-5 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-[var(--accent-color)] opacity-5 rounded-full blur-3xl transform translate-x-1/3 translate-y-1/3"></div>
      </div>
      
      {/* Confetti animation for celebration */}
      {showConfetti && <Confetti />}
      
      {/* Page Header */}
      <header className="mb-10 relative z-10">
        <h1 className="text-center text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[var(--primary-color)] to-[var(--interactive-color)] mb-3">
          Champions Arena
        </h1>
        <p className="text-center text-lg opacity-80 max-w-2xl mx-auto">
          Where legends are made. Compete with the best players and claim your spot at the top of the Quiz Arcade!
        </p>
      </header>
      
      {/* Top 3 Champions Section */}
      <section className="relative z-10">
        <TopThree topThree={topThree} isLightMode={isLightMode} />
      </section>

      {/* Search Bar and Leaderboard */}
      <section className="mt-12 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-center mx-auto w-full max-w-5xl mb-6 gap-4">
          <div className="relative w-full md:w-auto">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="h-5 w-5 text-[var(--secondary-color)]" />
            </div>
            <input
              type="text"
              placeholder="Search players..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full md:w-80 pl-10 pr-4 py-3 rounded-lg border bg-[var(--surface-color)] border-[var(--border-color)] text-[var(--text-color)] placeholder-[var(--secondary-color)] focus:outline-none focus:ring-2 focus:ring-[var(--interactive-color)] transition-all duration-300"
              aria-label="Search players"
            />
          </div>
          
          <button 
            className="px-5 py-3 bg-gradient-to-r from-[var(--interactive-color)] to-[var(--primary-color)] hover:brightness-110 text-white font-semibold rounded-lg transition-all duration-300 ease-in-out hover:shadow-lg flex items-center gap-2 w-full md:w-auto justify-center transform hover:translate-y-[-2px]"
            onClick={handleShare}
            aria-label="Share leaderboard"
          >
            <FaShare className="w-5 h-5" />
            Share Leaderboard
          </button>
        </div>
        
        <div className="bg-[var(--surface-color)] rounded-xl overflow-hidden mx-auto max-w-5xl shadow-lg border border-[var(--border-color)] backdrop-blur-sm bg-opacity-90">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-[var(--primary-color)] mb-4 flex items-center">
              <span className="bg-[var(--primary-color)] w-1 h-8 mr-3 rounded-full"></span>
              Global Rankings
            </h2>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[var(--surface-color-hover)] border-b-2 border-[var(--interactive-color)]">
                    <th className="p-4 text-xl font-bold text-[var(--primary-color)] text-left">Rank</th>
                    <th className="p-4 text-xl font-bold text-[var(--primary-color)] text-left">Player</th>
                    <th className="p-4 text-xl font-bold text-[var(--primary-color)] text-left">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeaderboard.slice(0, visibleUsers).map((user, index) => (
                    <tr 
                      key={index} 
                      className={`transition-all duration-200 hover:bg-[var(--surface-color-hover)] ${user.rank <= 3 ? 'bg-[var(--surface-color-hover)] bg-opacity-40' : ''} relative`}
                    >
                      <td className="p-4 text-lg border-b border-[var(--border-color)] text-left">
                        <div className="flex items-center">
                          {user.rank <= 3 ? (
                            <Trophy rank={user.rank} />
                          ) : (
                            <span className="ml-3">{user.rank}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-lg border-b border-[var(--border-color)] text-left">
                        <div className="flex items-center">
                          <div className="relative">
                          <img
                            className={`w-10 h-10 rounded-full object-cover mr-3 border-2 ${
                              user.rank === 1 
                                ? 'border-[var(--accent-color)]' 
                                : user.rank === 2 
                                  ? isLightMode
                                    ? 'border-[#2E3338]' // DARKER silver for light mode
                                    : 'border-[#B3B8C0]' // LIGHTER silver for dark mode 
                                  : user.rank === 3 
                                    ? 'border-amber-700'
                                    : 'border-[var(--border-color)]'
                            }`}
                            src={user.profilePic || '/profilepicture.png'}
                            alt={`${user.firstName} ${user.lastName}`}
                            onError={(e) => {e.target.src = '/profilepicture.png'}}
                          />
                          {user.rank <= 3 && (
                            <div className={`absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full ${
                              user.rank === 1 ? 'bg-[var(--accent-color)]' : 
                              user.rank === 2 ? isLightMode 
                                                ? 'bg-[#2E3338]'  // DARKER silver for light mode
                                                : 'bg-[#B3B8C0]'  // LIGHTER silver for dark mode
                                              : 'bg-amber-700'
                            } text-xs font-bold text-white`}>
                              {user.rank}
                            </div>
                          )}
                          </div>
                          <div>
                            <div className="font-medium">
                              {`${user.firstName} ${user.lastName}`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={`p-4 text-lg font-semibold border-b border-[var(--border-color)] text-left ${
                        user.rank === 1 
                          ? 'text-[var(--accent-color)]'
                          : user.rank === 2 
                            ? isLightMode 
                              ? 'text-[#2E3338]' // DARKER silver color for light mode
                              : 'text-[#B3B8C0]' // LIGHTER silver color for dark mode
                            : user.rank === 3 
                              ? 'text-amber-700'
                              : ''
                      }`}>
                        <div className="flex items-center">
                          {user.rank <= 3 && <FaFireAlt className="mr-2" aria-hidden="true" />}
                          {user.score.toLocaleString()} XP
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* View More/Less Button */}
            {filteredLeaderboard.length > 10 && (
              <div className="mt-6 text-center">
                <button 
                  className="px-6 py-3 bg-gradient-to-r from-[var(--interactive-color)] to-[var(--primary-color)] hover:brightness-110 text-white font-semibold rounded-lg transition-all duration-300 ease-in-out hover:shadow-lg flex items-center gap-2 mx-auto transform hover:translate-y-[-2px]"
                  onClick={showMoreUsers}
                  aria-label={showAll ? "Show fewer entries" : "Show all entries"}
                >
                  {showAll ? (
                    <>
                      <FaChevronUp className="w-5 h-5" />
                      Show Top Players
                    </>
                  ) : (
                    <>
                      <FaChevronDown className="w-5 h-5" />
                      View All Competitors ({filteredLeaderboard.length})
                    </>
                  )}
                </button>
              </div>
            )}
            
            {/* No Results Message */}
            {filteredLeaderboard.length === 0 && (
              <div className="text-center py-8">
                <p className="text-[var(--text-color)] opacity-70 text-lg">No players found matching your search.</p>
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Motivational Section */}
      <section className="mt-16 text-center max-w-3xl mx-auto relative z-10">
        <p className="text-[var(--text-color)] text-lg italic opacity-80">
          "Great achievement is born of great sacrifice, and is never the result of selfishness."
        </p>
        <p className="mt-2 text-[var(--primary-color)]">- Napoleon Hill</p>
      </section>
    </div>
  );
};

export default Leaderboard;